import { create } from 'zustand';
import {
  cloneFilterRules,
  draftFromSavedFilter,
  emptyRule,
  filterRecordFromDraft,
  sanitizeFilters,
} from '../Utils/holeQuery';
import {
  deleteHoleFilterRemote,
  fetchHoleFilters,
  postHoleFilter,
} from '../services/holeFilterApi';
import { readFilterSnapshot, readSchemaSnapshot, saveFilterSnapshot, saveSchemaSnapshot } from './holeOfflineCache';

const LS_KEY = 'mdv-hole-query';

function defaultDraft() {
  return {
    dataset: '',
    intervalMode: 'assay',
    combinator: 'AND',
    isPlan: false,
    rules: [emptyRule()],
  };
}

function splitRemoteFilters(rows) {
  const savedFilters = [];
  const sharedFilters = [];
  (rows || []).forEach((row) => {
    if (!row || !row.id || !row.name || row.builtin) return;
    const next = {
      ...row,
      shared: !!row.shared,
      builtin: false,
      rules: cloneFilterRules(row.rules),
    };
    if (next.shared) sharedFilters.push(next);
    else savedFilters.push(next);
  });
  return { savedFilters, sharedFilters };
}

function loadState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const schema = readSchemaSnapshot();
    if (!raw) return { draft: defaultDraft(), loadedLayers: [], pendingLocalFilters: [], schema };
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') {
      return { draft: defaultDraft(), loadedLayers: [], pendingLocalFilters: [], schema };
    }
    const pendingLocalFilters = Array.isArray(data.savedFilters)
      ? data.savedFilters.filter((row) => row && !row.builtin && !row.shared && row.id && row.name)
      : [];
    return {
      draft: { ...defaultDraft(), ...(data.draft || {}) },
      loadedLayers: Array.isArray(data.loadedLayers)
        ? data.loadedLayers.map((layer) => ({ ...layer, queryAutoLoad: false }))
        : [],
      pendingLocalFilters,
      schema,
    };
  } catch {
    return { draft: defaultDraft(), loadedLayers: [], pendingLocalFilters: [], schema: readSchemaSnapshot() };
  }
}

function persist(draft, loadedLayers, pendingLocalFilters) {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({
        draft,
        loadedLayers: (loadedLayers || []).map((layer) => ({ ...layer, queryAutoLoad: false })),
        savedFilters: (pendingLocalFilters || []).filter(
          (row) => row && !row.builtin && !row.shared && row.id && row.name
        ),
      })
    );
  } catch {
    // quota or private mode
  }
}

function findByName(list, name) {
  const label = String(name || '').trim().toLowerCase();
  if (!label) return null;
  return (list || []).find((row) => String(row.name).toLowerCase() === label) || null;
}

const boot = loadState();

export const useHoleQueryStore = create((set, get) => ({
  draft: boot.draft,
  loadedLayers: boot.loadedLayers,
  savedFilters: boot.pendingLocalFilters,
  sharedFilters: [],
  pendingLocalFilters: boot.pendingLocalFilters,
  builtinFilters: [],
  appliedFilterId: null,
  projectName: '',
  filtersLoading: false,
  filtersError: null,
  schema: boot.schema,
  schemaLoading: false,
  schemaError: null,

  setSchema: (schema) => {
    saveSchemaSnapshot(schema);
    set({ schema, schemaLoading: false, schemaError: null });
  },
  setSchemaLoading: (schemaLoading) => set({ schemaLoading }),
  setSchemaError: (schemaError) => set({ schemaLoading: false, schemaError }),

  hydrateBuiltins: (presets) => {
    const builtinFilters = Array.isArray(presets) ? presets : [];
    const prev = get().builtinFilters;
    if (
      prev.length === builtinFilters.length &&
      prev.every((row, i) => row.id === builtinFilters[i]?.id && row.name === builtinFilters[i]?.name)
    ) {
      return;
    }
    set({ builtinFilters });
  },

  hydrateRemoteFilters: async (projectName) => {
    const project = String(projectName || '').trim();
    if (!project) return;
    set({ projectName: project, filtersLoading: true, filtersError: null });
    const remote = await fetchHoleFilters(project);
    if (!remote.ok) {
      const snap = readFilterSnapshot(project);
      const pending = get().pendingLocalFilters.filter((row) => row && !row.shared);
      set({
        savedFilters: [
          ...pending,
          ...((snap?.savedFilters || []).filter((row) => !pending.some((local) => local.id === row.id))),
        ],
        sharedFilters: snap?.sharedFilters || [],
        filtersLoading: false,
        projectName: project,
      });
      return;
    }
    let { savedFilters, sharedFilters } = splitRemoteFilters(remote.filters);

    const pending = get().pendingLocalFilters.filter((row) => row && !row.shared);
    const leftover = [];
    for (const local of pending) {
      const already =
        savedFilters.some((row) => row.id === local.id) ||
        findByName(savedFilters, local.name) ||
        sharedFilters.some((row) => row.id === local.id);
      if (already) continue;
      try {
        const saved = await postHoleFilter(project, { ...local, shared: false });
        savedFilters = [{ ...saved, shared: false, builtin: false, rules: cloneFilterRules(saved.rules) }, ...savedFilters];
      } catch {
        leftover.push(local);
        savedFilters = [local, ...savedFilters];
      }
    }

    persist(get().draft, get().loadedLayers, leftover);
    set({
      savedFilters,
      sharedFilters,
      pendingLocalFilters: leftover,
      filtersLoading: false,
      projectName: project,
    });
    saveFilterSnapshot(project, { savedFilters, sharedFilters, schema: get().schema });
  },

  patchDraft: (patch) => {
    const draft = { ...get().draft, ...patch };
    persist(draft, get().loadedLayers, get().pendingLocalFilters);
    set({ draft, appliedFilterId: null });
  },

  resetDraft: () => {
    const draft = defaultDraft();
    persist(draft, get().loadedLayers, get().pendingLocalFilters);
    set({ draft, appliedFilterId: null });
  },

  applyFilter: (filter) => {
    if (!filter) return;
    const draft = draftFromSavedFilter(filter);
    persist(draft, get().loadedLayers, get().pendingLocalFilters);
    set({ draft, appliedFilterId: filter.id || null });
  },

  saveFilter: async (name, { shared = false } = {}) => {
    const label = String(name || '').trim();
    if (!label) return null;
    const project = get().projectName;
    if (!project) {
      throw new Error('Project is not ready');
    }
    const existing = shared
      ? findByName(get().sharedFilters.filter((row) => row.mine !== false), label)
      : findByName(get().savedFilters, label);
    const applied = [...get().savedFilters, ...get().sharedFilters].find(
      (row) => row.id === get().appliedFilterId
    );
    const reuseId =
      existing?.id ||
      (applied && !!applied.shared === !!shared && applied.mine !== false ? applied.id : null);
    const record = filterRecordFromDraft(get().draft, {
      id: reuseId,
      name: label,
      shared,
    });
    const saved = await postHoleFilter(project, record);
    const next = { ...record, ...saved, shared: !!shared, builtin: false, rules: cloneFilterRules(saved.rules || record.rules) };
    if (shared) {
      set({
        sharedFilters: [next, ...get().sharedFilters.filter((row) => row.id !== next.id)],
        savedFilters: get().savedFilters.filter((row) => row.id !== next.id),
        appliedFilterId: next.id,
      });
    } else {
      set({
        savedFilters: [next, ...get().savedFilters.filter((row) => row.id !== next.id)],
        sharedFilters: get().sharedFilters.filter((row) => row.id !== next.id),
        appliedFilterId: next.id,
      });
    }
    saveFilterSnapshot(project, {
      savedFilters: get().savedFilters,
      sharedFilters: get().sharedFilters,
      schema: get().schema,
    });
    return next;
  },

  shareFilter: async (filter, name) => {
    const project = get().projectName;
    if (!project) throw new Error('Project is not ready');
    const source = filter || [...get().savedFilters, ...get().sharedFilters].find((row) => row.id === get().appliedFilterId);
    const record = source
      ? {
          ...source,
          name: String(name || source.name || '').trim() || source.name,
          shared: true,
          builtin: false,
          rules: cloneFilterRules(source.rules),
        }
      : filterRecordFromDraft(get().draft, {
          name: String(name || '').trim(),
          shared: true,
        });
    if (!record.name) throw new Error('Name this filter before sharing');
    const saved = await postHoleFilter(project, record);
    const next = { ...record, ...saved, shared: true, builtin: false, rules: cloneFilterRules(saved.rules || record.rules) };
    set({
      sharedFilters: [next, ...get().sharedFilters.filter((row) => row.id !== next.id)],
      savedFilters: get().savedFilters.filter((row) => row.id !== next.id),
      appliedFilterId: next.id,
    });
    saveFilterSnapshot(project, {
      savedFilters: get().savedFilters,
      sharedFilters: get().sharedFilters,
      schema: get().schema,
    });
    return next;
  },

  deleteSavedFilter: async (id) => {
    if (!id || String(id).startsWith('builtin_')) return;
    const project = get().projectName;
    const localOnly = get().savedFilters.some((row) => row.id === id) && !project;
    if (!localOnly && project) {
      await deleteHoleFilterRemote(project, id);
    }
    set({
      savedFilters: get().savedFilters.filter((row) => row.id !== id),
      sharedFilters: get().sharedFilters.filter((row) => row.id !== id),
      appliedFilterId: get().appliedFilterId === id ? null : get().appliedFilterId,
    });
    saveFilterSnapshot(get().projectName, {
      savedFilters: get().savedFilters,
      sharedFilters: get().sharedFilters,
      schema: get().schema,
    });
  },

  addLoadedLayer: ({ dataset, filters, intervalMode, maxRows = 20000, filterName, filterId, shared = false, isPlan = false } = {}) => {
    const clean = sanitizeFilters(filters?.combinator, filters?.rules);
    const id =
      shared && filterId ? `sharedQuery_${filterId}` : `queryHoles_${Date.now().toString(36)}`;
    const label = String(filterName || '').trim() || 'Custom holes';
    const layer = {
      type: 'databaseHoles',
      name: id,
      displayName: label,
      filterName: label,
      filterId: filterId || undefined,
      dataset: dataset || undefined,
      interval_mode: intervalMode || 'assay',
      isplan: !!isPlan,
      collarQuery: clean,
      queryLayer: true,
      sharedQuery: !!shared,
      queryAutoLoad: true,
      max_rows: maxRows,
    };
    const loadedLayers = [layer, ...get().loadedLayers.filter((row) => row.name !== id)];
    persist(get().draft, loadedLayers, get().pendingLocalFilters);
    set({ loadedLayers });
    return layer;
  },

  setLoadedLayerIntervalMode: (name, mode) => {
    if (!name) return;
    const intervalMode = mode === 'lith1_code' ? 'lith1_code' : 'assay';
    const loadedLayers = get().loadedLayers.map((layer) => {
      if (layer.name !== name) return layer;
      const label = String(layer.filterName || layer.displayName || '').trim() || 'Custom holes';
      return {
        ...layer,
        interval_mode: intervalMode,
        displayName: label,
        filterName: label,
      };
    });
    persist(get().draft, loadedLayers, get().pendingLocalFilters);
    set({ loadedLayers });
  },

  removeLoadedLayer: (name) => {
    const loadedLayers = get().loadedLayers.filter((row) => row.name !== name);
    persist(get().draft, loadedLayers, get().pendingLocalFilters);
    set({ loadedLayers });
    try {
      const { useHoleDisplayStore } = require('./holeDisplayStore');
      useHoleDisplayStore.getState().closeLegend(name);
    } catch {
      // ignore
    }
  },
}));
